// File: ProfilePage.jsx
import React, { useEffect, useState, useRef } from "react";
import { FaCheckCircle, FaEdit, FaTimes, FaSave, FaPlane, FaCreditCard, FaCalendarCheck, FaReceipt, FaLinkedin, FaFacebook, FaInstagram, FaGithub, FaYoutube } from "react-icons/fa";
import Topbar from "../Components/Topbar";
import { toast } from "react-toastify";
import Sample14 from "../Components/Logo";
import gsap from "gsap";
import { getProfileData, updateProfileData } from "../Api/Profile";
import { FaUpload, FaTrash, FaEye, FaFilePdf } from 'react-icons/fa';
import DocumentCard from '../Components/DocumentCard';
import DocumentUploadModal from '../Components/DocumentUploadModal';
import { documentService } from '../Api/documentService';
import { experienceService } from '../Api/Profile';
import { useAuth } from "../Context/AuthContext";
import { Base_URL } from "../url";
import DocumentsSection from '../Components/DocumentsSection';
import { qualificationService } from "../Api/documentService";

const DEFAULTS = {
  department: "NA",
  designation: "NA",
  workLocation: "NA",
  employeeId: "NA",
  bankName: "NA",
  branch: "NA",
  employmentType: "NA"
};

// Delete Confirmation Modal Component
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, itemName }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const tl = gsap.timeline();
    tl.fromTo(modalRef.current,
      { opacity: 0, scale: 0.8 },
      { opacity: 1, scale: 1, duration: 0.4, ease: "back.out(1.7)" }
    );

    return () => tl.kill();
  }, [isOpen]);

  const handleClose = () => {
    const tl = gsap.timeline();
    tl.to(modalRef.current, { opacity: 0, scale: 0.8, duration: 0.3 })
      .eventCallback("onComplete", onClose);
  };

  if (!isOpen) return null;

  return (
    <div className="profile-modal-overlay" ref={modalRef}>
      <div className="profile-modal-content profile-confirm-modal">
        <div className="profile-modal-header">
          <h3>Confirm Deletion</h3>
          <button className="profile-close-btn" onClick={handleClose}>
            <FaTimes />
          </button>
        </div>
        
        <div className="profile-confirm-body">
          <p>Are you sure you want to delete {itemName}? This action cannot be undone.</p>
        </div>
        
        <div className="profile-form-actions">
          <button type="button" onClick={handleClose} className="profile-btn-secondary">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} className="profile-btn-danger">
            <FaTrash /> Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// Form Modal Component
const FormModal = ({ isOpen, onClose, formType, initialData, onSubmit }) => {
  const modalRef = useRef(null);
  const formRef = useRef(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  useEffect(() => {
    if (!isOpen) return;

    const tl = gsap.timeline();
    tl.fromTo(modalRef.current,
      { opacity: 0, scale: 0.8 },
      { opacity: 1, scale: 1, duration: 0.4, ease: "back.out(1.7)" }
    ).fromTo(formRef.current,
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.3 },
      "-=0.2"
    );

    return () => tl.kill();
  }, [isOpen]);

  const handleClose = () => {
    const tl = gsap.timeline();
    tl.to(formRef.current, { y: 30, opacity: 0, duration: 0.2 })
      .to(modalRef.current, { opacity: 0, scale: 0.8, duration: 0.3 }, "-=0.1")
      .eventCallback("onComplete", onClose);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onSubmit(formData);
    setLoading(false);
    handleClose();
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  if (!isOpen) return null;

  const formConfigs = {
    basic: {
      title: "Edit Basic Details",
      fields: [
        { name: "employeeId", label: "Employee ID", type: "text", readonly: true },
        { name: "department", label: "Department", type: "text", readonly: true },
        { name: "role", label: "Role", type: "text", readonly: true },
        { name: "employeeType", label: "Employee Type", type: "text", readonly: true },
        { name: "location", label: "Location", type: "text", readonly: false },
        { name: "doj", label: "Date Of Joining", type: "text", readonly: true }
      ]
    },
    contact: {
      title: "Edit Contact Information",
      fields: [
        { name: "email", label: "Email", type: "email", readonly: true },
        { name: "phoneNumber", label: "Phone Number", type: "tel", readonly: false },
        { name: "emergencyContactName", label: "Emergency Contact Name", type: "text", readonly: false },
        { name: "emergencyContactRelation", label: "Emergency Contact Relation", type: "text", readonly: false },
        { name: "emergencyContactPhone", label: "Emergency Contact Phone", type: "tel", readonly: false }
      ]
    },
    family: {
      title: "Edit Family & Personal Details",
      fields: [
        { name: "fathersName", label: "Father's Name", type: "text", readonly: false },
        { name: "maritalStatus", label: "Marital Status", type: "select", readonly: false, options: ["Single", "Married", "Divorced", "Widowed"] },
        { name: "bloodGroup", label: "Blood Group", type: "select", readonly: false, options: ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"] },
        { name: "education", label: "Education", type: "text", readonly: false },
        { name: "panNumber", label: "PAN Number", type: "text", readonly: false },
        { name: "aadharNumber", label: "Aadhaar Number", type: "text", readonly: false },
        { name: "spouseName", label: "Spouse Name", type: "text", readonly: false },
        { name: "uanNumber", label: "UAN Number", type: "text", readonly: false },
        { name: "address1", label: "Current Address 1", type: "text", readonly: false },
        { name: "address2", label: "Current Address 2", type: "text", readonly: false },
        { name: "address3", label: "Current Address 3", type: "text", readonly: false },
        { name: "address4", label: "Permanent Address 1", type: "text", readonly: false },
        { name: "address5", label: "Permanent Address 2", type: "text", readonly: false },
        { name: "address6", label: "Permanent Address 3", type: "text", readonly: false }
      ]
    },
    bank: {
      title: "Edit Bank Information",
      fields: [
        { name: "bankName", label: "Bank Name", type: "text", readonly: false },
        { name: "accountNumber", label: "Account Number", type: "text", readonly: false },
        { name: "ifscCode", label: "IFSC Code", type: "text", readonly: false },
        { name: "netSalary", label: "Net Salary", type: "text", readonly: true },
        { name: "pfDeduction", label: "PF Deduction", type: "text", readonly: true },
        { name: "tdsDeduction", label: "TDS Deduction", type: "text", readonly: true }
      ]
    },
    attendance: {
      title: "Update Attendance Preferences",
      fields: [
        { name: "salaryDays", label: "Salary Days", type: "number", readonly: true },
        { name: "leaveAllowed", label: "Leave Allowed", type: "number", readonly: true },
        { name: "leaveTaken", label: "Leave Taken", type: "number", readonly: true },
        { name: "remainingLeave", label: "Remaining Leave", type: "number", readonly: true },
        { name: "notificationPreference", label: "Leave Notification", type: "select", readonly: false, options: ["Disabled", "Email Only", "SMS Only", "Both"] }
      ]
    },
    assets: {
      title: "Update Asset Details",
      fields: [
        { name: "assetType", label: "Asset Type", type: "text", readonly: true },
        { name: "brandModel", label: "Brand & Model", type: "text", readonly: true },
        { name: "serialNo", label: "Serial Number", type: "text", readonly: true },
        { name: "assetCategory", label: "Category", type: "text", readonly: true },
        { name: "assetCondition", label: "Condition", type: "select", readonly: false, options: ["Excellent", "Good", "Fair", "Poor", "Needs Repair"] },
        { name: "warrantyExpiry", label: "Warranty Expiry", type: "date", readonly: true },
        { name: "notes", label: "Additional Notes", type: "textarea", readonly: false }
      ]
    },
    documents: {
      title: "Document Information",
      fields: [
        { name: "documentType", label: "Document Type", type: "text", readonly: true },
        { name: "documentNumber", label: "Document Number", type: "text", readonly: false },
        { name: "issueDate", label: "Issue Date", type: "date", readonly: false },
        { name: "expiryDate", label: "Expiry Date", type: "date", readonly: false },
        { name: "issuingAuthority", label: "Issuing Authority", type: "text", readonly: false }
      ]
    },
    experience: {
      title: "Add Work Experience",
      fields: [
        { name: "previousCompanyName", label: "Company Name", type: "text", readonly: false },
        { name: "previousDesignation", label: "Designation", type: "text", readonly: false },
        { name: "experience", label: "Experience (years)", type: "number", readonly: false, step: "0.1" },
        { name: "startDate", label: "Start Date", type: "date", readonly: false },
        { name: "endDate", label: "End Date", type: "date", readonly: false }
      ]
    },
    'experience-edit': {
      title: "Edit Work Experience",
      fields: [
        { name: "previousCompanyName", label: "Company Name", type: "text", readonly: false },
        { name: "previousDesignation", label: "Designation", type: "text", readonly: false },
        { name: "experience", label: "Experience (years)", type: "number", readonly: false, step: "0.1" },
        { name: "startDate", label: "Start Date", type: "date", readonly: false },
        { name: "endDate", label: "End Date", type: "date", readonly: false }
      ]
    },
    socialMedia: {
      title: "Edit Social Media Links",
      fields: [
        { name: "linkedIn", label: "LinkedIn URL", type: "url", readonly: false, placeholder: "https://linkedin.com/in/username" },
        { name: "facebook", label: "Facebook URL", type: "url", readonly: false, placeholder: "https://facebook.com/username" },
        { name: "instagram", label: "Instagram URL", type: "url", readonly: false, placeholder: "https://instagram.com/username" },
        { name: "github", label: "GitHub URL", type: "url", readonly: false, placeholder: "https://github.com/username" },
        { name: "youtube", label: "Youtube URL", type: "url", readonly: false, placeholder: "https://youtube.com/username" },
      ]
    }
  };

  const config = formConfigs[formType] || formConfigs.basic;

  return (
    <div className="profile-modal-overlay" ref={modalRef}>
      <div className="profile-modal-content" ref={formRef}>
        <div className="profile-modal-header">
          <h2>{config.title}</h2>
          <button className="profile-close-btn" onClick={handleClose}>
            <FaTimes />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="profile-modal-form">
          <div className="profile-form-grid">
            {config.fields.map(field => (
              <div key={field.name} className={`profile-form-field ${field.readonly ? 'profile-readonly' : ''}`}>
                <label>{field.label}</label>
                {field.type === 'textarea' ? (
                  <textarea
                    name={field.name}
                    value={formData[field.name] || ''}
                    onChange={handleChange}
                    readOnly={field.readonly}
                    className={field.readonly ? 'profile-readonly-field' : ''}
                    rows="3"
                    placeholder={field.placeholder || ''}
                  />
                ) : field.type === 'select' ? (
                  <select
                    name={field.name}
                    value={formData[field.name] || ''}
                    onChange={handleChange}
                    disabled={field.readonly}
                    className={field.readonly ? 'profile-readonly-field' : ''}
                  >
                    <option value="">Select {field.label}</option>
                    {field.options?.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={field.type}
                    name={field.name}
                    value={formData[field.name] || ''}
                    onChange={handleChange}
                    readOnly={field.readonly}
                    className={field.readonly ? 'profile-readonly-field' : ''}
                    placeholder={field.placeholder || ''}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="profile-form-actions">
            <button 
              type="button" 
              onClick={handleClose} 
              className="profile-btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={loading} 
              className="profile-btn-primary"
            >
              <FaSave /> {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function ProfilePage() {
  const [userDetails, setUserDetails] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeForm, setActiveForm] = useState('');
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiLoading, setApiLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("personal");
  const { user } = useAuth(); 
  const [leaveInfoData, setLeaveInfoData] = useState(null);
  const [leaveInfoLoading, setLeaveInfoLoading] = useState(false);
  const [leaveInfoError, setLeaveInfoError] = useState(null);
  console.log(user);

  const [documentUploadModal, setDocumentUploadModal] = useState({
    isOpen: false,
    documentType: ''
  });
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    itemName: '',
    onConfirm: null
  });

  const cardRef = useRef(null);
  const tabsRef = useRef(null);
  const avatarRef = useRef(null);
  const borderRef = useRef(null);



  useEffect(() => {
    if (profileData?.resource?.mav_UserID) {
      loadUserDocuments();
    }
  }, [profileData]);
  
  useEffect(() => {
    if (activeTab === "leaveInfo" && !leaveInfoData && !leaveInfoLoading) {
      fetchLeaveInfo();
    }
  }, [activeTab]);
  
  const fetchLeaveInfo = async () => {
    if (!user?.userId) return;

    try {
      setLeaveInfoLoading(true);
      setLeaveInfoError(null);

      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");

      const res = await fetch(`${Base_URL}/Leave/LeaveBalance/${user.userId}/${year}/${month}`);
      if (!res.ok) throw new Error("Failed to fetch leave info");

      const data = await res.json();
      setLeaveInfoData(data);
    } catch (err) {
      console.error("Error fetching leave info:", err);
      setLeaveInfoError(err.message || "Something went wrong");
    } finally {
      setLeaveInfoLoading(false);
    }
  };

  // Helper function to map frontend data to backend DTO structure
  const mapFormDataToBackendPayload = (formType, formData, currentProfileData) => {
    const payload = {};
    
    switch (formType) {
      case 'contact':
        payload.Resource = {
          phoneNumber: formData.phoneNumber || currentProfileData?.resource?.phoneNumber,
          emergencyContactName: formData.emergencyContactName || currentProfileData?.resource?.emergencyContactName,
          emergencyContactRelation: formData.emergencyContactRelation || currentProfileData?.resource?.emergencyContactRelation,
          emergencyContactPhoneNumber: formData.emergencyContactPhone || currentProfileData?.resource?.emergencyContactPhoneNumber
        };
        break;
        
      case 'family':
        payload.PersonalDetails = {
          fathersName: formData.fathersName || currentProfileData?.resource?.fathersName,
          spouseName: formData.spouseName || currentProfileData?.resource?.spouseName,
          maritalStatus: formData.maritalStatus || currentProfileData?.resource?.maritalStatus,
          bloodGroup: formData.bloodGroup || currentProfileData?.resource?.bloodGroup,
          education: formData.education || currentProfileData?.resource?.education,
          panNumber: formData.panNumber || currentProfileData?.resource?.panNumber,
          aadharNumber: formData.aadharNumber || currentProfileData?.resource?.aadharNumber,
          uanNumber: formData.uanNumber || currentProfileData?.resource?.uanNumber,
          address1: formData.address1 || currentProfileData?.address?.address1,
          address2: formData.address2 || currentProfileData?.address?.address2,
          address3: formData.address3 || currentProfileData?.address?.address3,
          address4: formData.address4 || currentProfileData?.address?.address4,
          address5: formData.address5 || currentProfileData?.address?.address5,
          address6: formData.address6 || currentProfileData?.address?.address6,
        };
        break;
        
      case 'bank':
        payload.Resource = {
          bankName: formData.bankName || currentProfileData?.payroll?.bankName,
          accountNo: formData.accountNumber || currentProfileData?.payroll?.accountNo,
          ifscCode: formData.ifscCode || currentProfileData?.payroll?.ifscCode
        };
        
        // Only include payroll data if it exists in current profile
        if (currentProfileData?.payroll) {
          payload.Payroll = {
            netPayable: formData.netSalary ? parseFloat(formData.netSalary.replace(/[^0-9.-]+/g, "")) : null,
            pf: formData.pfDeduction ? parseFloat(formData.pfDeduction.replace(/[^0-9.-]+/g, "")) : null,
            tdsdeductionAmount: formData.tdsDeduction ? parseFloat(formData.tdsDeduction.replace(/[^0-9.-]+/g, "")) : null
          };
        }
        break;
        
      case 'assets':
        if (currentProfileData?.assetsDetails?.[0]) {
          payload.AssetsDetails = [{
            assetId: currentProfileData.assetsDetails[0].assetId,
            asset_Condition: formData.assetCondition || currentProfileData.assetsDetails[0].asset_Condition,
            remarks: formData.notes || currentProfileData.assetsDetails[0].remarks || "",
            purchaseDate: formData.purchaseDate ? new Date(formData.purchaseDate) : null
          }];
        }
        break;
        
      case 'basic':
        // Update location if it's different
        if (formData.location && formData.location !== currentProfileData?.resource?.defaultLocation) {
          payload.Resource = {
            defaultLocation: formData.location // Ensure this key matches what your backend expects
          };
        }
        break;
        
      case 'socialMedia':
        payload.Resource = {
          linkedIn: formData.linkedIn || currentProfileData?.resource?.linkedIn,
          facebook: formData.facebook || currentProfileData?.resource?.facebook,
          instagram: formData.instagram || currentProfileData?.resource?.instagram,
          github: formData.github || currentProfileData?.resource?.github,
          youtube: formData.youtube || currentProfileData?.resource?.youtube
        };
        break;
        
      default:
        console.warn('Unknown form type:', formType);
    }
    
    // Remove empty objects
    Object.keys(payload).forEach(key => {
      if (payload[key] && Object.keys(payload[key]).length === 0) {
        delete payload[key];
      }
    });
    
    return payload;
  };

  const calculateLeaveInfo = (attendanceData) => {
    const totalLeaveAllowed = 1.5;
    const leaveTaken = parseFloat(attendanceData?.leave || 0);
    const remainingLeave = totalLeaveAllowed - leaveTaken;
    const isOverLimit = leaveTaken > totalLeaveAllowed;
    
    return {
      totalLeaveAllowed,
      leaveTaken,
      remainingLeave: Math.max(0, remainingLeave),
      isOverLimit
    };
  };

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setApiLoading(true);
        const data = await getProfileData();
        setProfileData(data);
        
        const stored = JSON.parse(localStorage.getItem("userDetails")) || {};
        const merged = { 
          ...DEFAULTS, 
          ...stored,
          employeeId: data.resource?.resourceId,
          username: data.resource?.name || stored.username,
          department: data.resource?.department,
          designation: data.resource?.role,
          employmentType: data.resource?.employeeType,
          phone: data.resource?.phoneNumber,
          email: data.resource?.emailId || stored.email,
          bankName: data.payroll?.bankName,
          bankAccount: data.payroll?.accountNo,
          ifsc: data.payroll?.ifscCode,
          monthlySalary: data.payroll?.netPayable,
          attendanceThisMonth: data.attendance?.salaryDays,
          leaveBalance: data.attendance?.leave
        };
        
        setUserDetails(merged);
        setFormData(merged);
      } catch (error) {
        console.error("Error fetching profile data:", error);
        toast.error("Failed to load profile data");
        const stored = JSON.parse(localStorage.getItem("userDetails")) || {};
        const merged = { ...DEFAULTS, ...stored };
        if (!Array.isArray(merged.roles)) merged.roles = merged.roles ? [merged.roles] : [];
        setUserDetails(merged);
        setFormData(merged);
      } finally {
        setApiLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  useEffect(() => {
    if (!userDetails) return;
    
    const tl = gsap.timeline({ defaults: { duration: 0.6, ease: "power3.out" } });
    if (cardRef.current) tl.from(cardRef.current, { y: 20, opacity: 0, scale: 0.995 });
    if (tabsRef.current) tl.from(tabsRef.current, { y: 8, opacity: 0 }, "<");
    if (avatarRef.current) tl.from(avatarRef.current, { scale: 0.6, opacity: 0 }, "<0.1");
    
    if (borderRef.current) {
      gsap.fromTo(borderRef.current, 
        { scale: 0.8, opacity: 0 },
        { scale: 1, opacity: 1, duration: 1, ease: "elastic.out(1, 0.5)" }
      );
    }
    
    return () => tl.kill();
  }, [userDetails]);

  useEffect(() => {
    const handleMove = (e) => {
      if (!avatarRef.current || !borderRef.current) return;
      
      const rect = avatarRef.current.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) / 40;
      const dy = (e.clientY - cy) / 40;
      
      gsap.to(avatarRef.current, { x: dx, y: dy, duration: 0.4, ease: "power2.out" });
      
      const angle = Math.atan2(dy, dx) * (180 / Math.PI);
      gsap.to(borderRef.current, { 
        rotation: angle, 
        duration: 0.8,
        ease: "power2.out"
      });
    };
    
    window.addEventListener("mousemove", handleMove);
    return () => window.removeEventListener("mousemove", handleMove);
  }, []);

  const handleTabClick = (id) => {
    setActiveTab(id);
    
    if (cardRef.current) {
      gsap.fromTo(cardRef.current, 
        { y: 10, opacity: 0.8 }, 
        { y: 0, opacity: 1, duration: 0.45, ease: "power2.out" }
      );
    }
    
    const activeBtn = document.querySelector(`.profile-tab-btn[data-tab="${id}"]`);
    if (activeBtn) {
      gsap.fromTo(activeBtn.querySelector('.profile-tab-dot'),
        { scale: 0, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.3, ease: "back.out(1.7)" }
      );
    }
  };

// In Profile page
const handleDocumentUpload = async (documentType, file) => {
  try {
    // Get user ID from localStorage
    const userDetailsStr = localStorage.getItem('userDetails');
    if (!userDetailsStr) {
      toast.error('Please log in first');
      return;
    }
    
    const userDetails = JSON.parse(userDetailsStr);
    const userId = userDetails.userId;
    
    if (!userId) {
      toast.error("User ID not available");
      return;
    }

    const result = await documentService.uploadDocument(userId, documentType, file);
    toast.success("Document Uploaded Successfully");
    await loadUserDocuments();
  } catch (error) {
    toast.error(`Upload failed: ${error.message}`);
  }
};

  const handleDocumentDelete = async (documentId) => {
    console.group('🗑️ Document Delete Handler');
    try {
      console.log('Deleting document:', documentId);
      
      await documentService.deleteDocument(documentId);
      console.log('Delete successful');
      
      // Update documents state to mark this document as not uploaded
      setDocuments(prevDocs => 
        prevDocs.map(doc => 
          doc.documentId === documentId 
            ? { ...doc, url: null, fileName: null, uploaded: false }
            : doc
        )
      );
      
      toast.success('Document deleted successfully!');
    } catch (error) {
      console.error('Delete handler error:', error);
      toast.error('Failed to delete document');
      throw error;
    } finally {
      console.groupEnd();
    }
  };

  const handleDocumentView = (document) => {
    console.group('👀 Document View Handler');
    console.log('Opening document:', document);
    
    if (document.url) {
      window.open(document.url, '_blank', 'noopener,noreferrer');
    } else {
      console.warn('No URL available for document');
      toast.error('Document URL not available');
    }
    console.groupEnd();
  };

const getDefaultDocuments = () => {
  return [
    { documentId: 'pan', documentType: 'PAN Card', url: null, fileName: null, uploaded: false },
    { documentId: 'aadhar', documentType: 'Aadhaar Card', url: null, fileName: null, uploaded: false },
    { documentId: 'passport', documentType: 'Passport', url: null, fileName: null, uploaded: false },
    { documentId: 'cancelled-cheque', documentType: 'Cancelled Cheque', url: null, fileName: null, uploaded: false },
    { documentId: 'bank-passbook', documentType: 'Bank Passbook', url: null, fileName: null, uploaded: false },
    { documentId: 'bank-statement', documentType: 'Bank Statement', url: null, fileName: null, uploaded: false }
  ];
};

const loadUserDocuments = async () => {
  setDocumentsLoading(true);
  try {
    const userId = profileData?.resource?.mav_UserID || 5;
    console.log('🔄 Loading documents for user:', userId);
    
    const response = await documentService.getUserDocuments(userId);
    console.log('📥 API response:', response);
    
    // Check if response has the expected structure
    let apiDocuments = [];
    if (response.success && Array.isArray(response.documents)) {
      apiDocuments = response.documents;
    } else if (Array.isArray(response)) {
      apiDocuments = response;
    }
    
    console.log('📄 Extracted documents:', apiDocuments);
    
    // Define all expected document types
    const expectedDocumentTypes = [
      'PAN Card',
      'Aadhaar Card',
      'Passport',
      'Cancelled Cheque',
      'Bank Passbook',
      'Bank Statement'
    ];
    
    // Create a map of existing documents by type
    const uploadedDocsMap = {};
    apiDocuments.forEach(doc => {
      const docType = doc.documentType?.trim();
      uploadedDocsMap[docType] = {
        documentId: doc.documentId,
        url: doc.url,
        fileName: doc.fileName || doc.documentType,
        uploaded: true
      };
    });
    
    // Create final documents array with all 6 types
    const finalDocuments = expectedDocumentTypes.map(docType => {
      const uploadedDoc = uploadedDocsMap[docType];
      
      if (uploadedDoc) {
        return {
          documentId: uploadedDoc.documentId,
          documentType: docType,
          url: uploadedDoc.url,
          fileName: uploadedDoc.fileName,
          uploaded: true
        };
      }
      
      // Return default for unuploaded documents
      return {
        documentId: docType.toLowerCase().replace(/\s+/g, '-'),
        documentType: docType,
        url: null,
        fileName: null,
        uploaded: false
      };
    });
    
    console.log('✅ Final mapped documents:', finalDocuments);
    setDocuments(finalDocuments);
    
  } catch (error) {
    console.error('❌ Error loading documents:', error);
    // Initialize with default documents if API fails
    const defaultDocs = [
      { documentId: 'pan-card', documentType: 'PAN Card', url: null, fileName: null, uploaded: false },
      { documentId: 'aadhaar-card', documentType: 'Aadhaar Card', url: null, fileName: null, uploaded: false },
      { documentId: 'passport', documentType: 'Passport', url: null, fileName: null, uploaded: false },
      { documentId: 'cancelled-cheque', documentType: 'Cancelled Cheque', url: null, fileName: null, uploaded: false },
      { documentId: 'bank-passbook', documentType: 'Bank Passbook', url: null, fileName: null, uploaded: false },
      { documentId: 'bank-statement', documentType: 'Bank Statement', url: null, fileName: null, uploaded: false }
    ];
    setDocuments(defaultDocs);
  } finally {
    setDocumentsLoading(false);
  }
};

  // Update the useEffect for documents
  useEffect(() => {
    if (profileData?.resource?.mav_UserID) {
      console.log('👤 User ID available, loading documents...');
      loadUserDocuments();
    } else {
      console.log('⏳ Waiting for user ID to load documents...');
    }
  }, [profileData?.resource?.mav_UserID]);

  // Add to your ProfilePage component
  const handleDeleteExperience = async (expId) => {
    const experienceToDelete = profileData?.experienceDetails?.find(exp => exp.expId === expId);
    const itemName = experienceToDelete ? `experience at ${experienceToDelete.previousCompanyName}` : 'this experience';
    
    setDeleteModal({
      isOpen: true,
      itemName: itemName,
      onConfirm: async () => {
        try {
          const userDetails = JSON.parse(localStorage.getItem('userDetails'));
          const mavUserId = userDetails.userId;
          
          await experienceService.deleteExperience(mavUserId, expId);
          
          // Refresh profile data
          const freshData = await getProfileData();
          setProfileData(freshData);
          
          toast.success("Experience deleted successfully");
          setDeleteModal({ isOpen: false, itemName: '', onConfirm: null });
        } catch (error) {
          console.error('Error deleting experience:', error);
          toast.error("Failed to delete experience");
          setDeleteModal({ isOpen: false, itemName: '', onConfirm: null });
        }
      }
    });
  };

  // Update your handleFormSubmit for experience operations
  const handleFormSubmit = async (formData) => {
    setLoading(true);
    try {
      if (activeForm === 'experience') {
        const userDetails = JSON.parse(localStorage.getItem('userDetails'));
        const mavUserId = userDetails.userId;
        
        const experienceData = {
          previousCompanyName: formData.previousCompanyName,
          previousDesignation: formData.previousDesignation,
          experience: parseFloat(formData.experience),
          startDate: formData.startDate,
          endDate: formData.endDate
        };
        
        await experienceService.addExperience(mavUserId, experienceData);
        toast.success("Experience added successfully");
        
        // Refresh profile data
        const freshData = await getProfileData();
        setProfileData(freshData);
      } 
      else if (activeForm === 'experience-edit') {
        // Your existing PUT operation will handle this through updateProfileData
        const updatePayload = {
          ExperienceDetails: [{
            expId: formData.expId,
            previousCompanyName: formData.previousCompanyName,
            previousDesignation: formData.previousDesignation,
            experience: parseFloat(formData.experience),
            startDate: formData.startDate,
            endDate: formData.endDate
          }]
        };
        
        await updateProfileData(updatePayload);
        const freshData = await getProfileData();
        setProfileData(freshData);
        toast.success("Experience updated successfully");
      }
      else {
        // Your existing update logic for other forms
        const updatePayload = mapFormDataToBackendPayload(activeForm, formData, profileData);
        
        if (Object.keys(updatePayload).length > 0) {
          await updateProfileData(updatePayload);
          const freshData = await getProfileData();
          setProfileData(freshData);
          toast.success("Profile Updated Successfully");
        } else {
          toast.info("No changes detected");
        }
      }
    } catch (err) {
      console.error('Update error:', err);
      toast.error("Error updating: " + (err.message || "Please try again"));
    } finally {
      setLoading(false);
    }
  };

  const capitalizeWords = (str) => {
    if (!str) return '';
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const makeUppercase = (str) => {
    if (!str || typeof str !== 'string') return '';
    return str.toUpperCase();
  };

  const handleDocumentClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const tabs = [
    { id: "personal", label: "Personal Info", icon: "👤" },
    { id: "bank", label: "Bank & Payroll", icon: "💳" },
    { id: "attendance", label: "Attendance", icon: "📊" },
    { id: "expenses", label: "Expenses", icon: "💰" },
    { id: "travel", label: "Travel", icon: "✈️" },
    { id: "approvals", label: "Approvals", icon: "✅" },
    { id: "documents", label: "Documents", icon: "📑" },
    { id: "assets", label: "Assets", icon: "🛠️" },
    { id: "leaveInfo", label: "Leave Info", icon: "📝" },
    { id: "socialMedia", label: "Social Media", icon: "🌐" } // New tab added here
  ];

  const val = (k) => (userDetails?.[k] === undefined || userDetails[k] === null || userDetails[k] === "" ? "NA" : userDetails[k]);

  if (apiLoading) return <div className="profile-loading">Loading profile data...</div>;
  if (!userDetails) return <div className="profile-loading">No user data found...</div>;

  const leaveInfo = calculateLeaveInfo(profileData?.attendance);

  // Function to get platform icon
  const getSocialMediaIcon = (platform) => {
    switch(platform) {
      case 'linkedIn': return <FaLinkedin />;
      case 'facebook': return <FaFacebook />;
      case 'instagram': return <FaInstagram />;
      case 'github': return <FaGithub />;
      case 'youtube': return <FaYoutube />;
      default: return null;
    }
  };

  // Function to get platform color
  const getPlatformColor = (platform) => {
    switch(platform) {
      case 'linkedIn': return '#0077b5';
      case 'facebook': return '#1877f2';
      case 'instagram': return '#e4405f';
      case 'github': return '#333';
      case 'youtube': return '#ff0000';
      default: return '#6c757d';
    }
  };

  // Function to get platform display name
  const getPlatformDisplayName = (platform) => {
    switch(platform) {
      case 'linkedIn': return 'LinkedIn';
      case 'facebook': return 'Facebook';
      case 'instagram': return 'Instagram';
      case 'github': return 'GitHub';
      case 'youtube': return 'YouTube';
      default: return platform;
    }
  };

  return (
    <div className="profile-main-content profile-expand">
      {/* <Topbar /> */}

      <div className="profile-dashboard-overview-container profile-root">
        <h1 className="profile-page-title">My Profile</h1>

        <div className="profile-card" ref={cardRef}>
          <div className="profile-animated-border" ref={borderRef}></div>
          
          <div className="profile-header">
            <div className="profile-avatar-container">
              <div className="profile-avatar" ref={avatarRef} title={val("username")}>
                {val("username")[0] ? val("username").charAt(0).toUpperCase() : "U"}
              </div>
              <div className="profile-avatar-glow"></div>
            </div>
            <div className="profile-name-block">
              <h2 className="profile-name">
                {profileData?.resource?.name || val("username")}
              </h2>
              <p className="profile-mail">{profileData?.resource?.emailId || val("email")}</p>
              <p className="profile-designation">  {capitalizeWords(profileData?.role?.description)} • {makeUppercase(profileData?.department?.description)}</p>
            </div>
          </div>

          <div className="profile-nav-tabs" ref={tabsRef}>
            <div className="profile-tabs-row">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  className={`profile-tab-btn ${activeTab === t.id ? "profile-active" : ""}`}
                  onClick={() => handleTabClick(t.id)}
                  data-tab={t.id}
                >
                  <span className="profile-tab-icon">{t.icon}</span>
                  {t.label}
                  <div className="profile-tab-dot"></div>
                  <div className="profile-tab-line"></div>
                </button>
              ))}
            </div>
          </div>

          <div className="profile-details">
            {activeTab === "personal" ? (
              <>
                <div className="profile-info-card">
                  <div className="profile-card-header">
                    <h3>Basic Details</h3>
                    <button 
                      className="profile-edit-icon-btn"
                      onClick={() => {
                        setActiveForm('basic');
                        setFormData({
                          employeeId: profileData?.resource?.resourceId,
                          department: profileData?.resource?.department,
                          role: profileData?.resource?.role,
                          employeeType: profileData?.resource?.employeeType,
                          location: profileData?.resource?.defaultLocation,
                          doj: profileData?.resource?.doj?.split('T')[0]
                        });
                        setIsModalOpen(true);
                      }}
                    >
                      <FaEdit />
                    </button>
                  </div>
                  <div className="profile-card-body">
                    <div className="profile-detail-item">
                      <span>Employee ID</span>
                      <p>{profileData?.resource?.resourceId}</p>
                    </div>
                    <div className="profile-detail-item">
                      <span>Department</span>
                      <p>{makeUppercase(profileData?.department?.description)}</p>
                    </div>
                    <div className="profile-detail-item">
                      <span>Role</span>
                      <p>{capitalizeWords(profileData?.role?.description)}</p>
                    </div>
                    <div className="profile-detail-item">
                      <span>Employee Type</span>
                      <p>{profileData?.employeeType?.description}</p>
                    </div>
                    <div className="profile-detail-item">
                      <span>Location</span>
                      <p>{profileData?.resource?.defaultLocation}</p>
                    </div>
                    <div className="profile-detail-item">
                      <span>Date Of Joining</span>
                      <p>{profileData?.resource?.doj?.split('T')[0]}</p>
                    </div>
                  </div>
                </div>

                <div className="profile-info-card">
                  <div className="profile-card-header">
                    <h3>Contact Information</h3>
                    <button 
                      className="profile-edit-icon-btn"
                      onClick={() => {
                        setActiveForm('contact');
                        setFormData({
                          email: profileData?.resource?.emailId,
                          phoneNumber: profileData?.resource?.phoneNumber,
                          emergencyContactName: profileData?.resource?.emergencyContactName,
                          emergencyContactRelation: profileData?.resource?.emergencyContactRelation,
                          emergencyContactPhone: profileData?.resource?.emergencyContactPhoneNumber
                        });
                        setIsModalOpen(true);
                      }}
                    >
                      <FaEdit />
                    </button>
                  </div>
                  <div className="profile-card-body">
                    <div className="profile-detail-item">
                      <span>Email</span>
                      <p>{profileData?.resource?.emailId}</p>
                    </div>
                    <div className="profile-detail-item">
                      <span>Phone</span>
                      <p>{profileData?.resource?.phoneNumber}</p>
                    </div>
                    <div className="profile-detail-item">
                      <span>Emergency Contact</span>
                      <p>{profileData?.resource?.emergencyContactName} ({profileData?.resource?.emergencyContactRelation})</p>
                      <p className="profile-contact-sub">{profileData?.resource?.emergencyContactPhoneNumber}</p>
                    </div>
                  </div>
                </div>

                <div className="profile-info-card">
                  <div className="profile-card-header">
                    <h3>Family & Personal Details</h3>
                    <button 
                      className="profile-edit-icon-btn"
                      onClick={() => {
                        setActiveForm('family');
                        setFormData({
                          fathersName: profileData?.resource?.fathersName,
                          spouseName: profileData?.resource?.spouseName,
                          maritalStatus: profileData?.resource?.maritalStatus,
                          bloodGroup: profileData?.resource?.bloodGroup,
                          education: profileData?.resource?.education,
                          panNumber: profileData?.resource?.panNumber,
                          aadharNumber: profileData?.resource?.aadharNumber,
                          uanNumber: profileData?.resource?.uanNumber,
                          address1: profileData?.address?.address1,
                          address2: profileData?.address?.address2,
                          address3: profileData?.address?.address3,
                          address4: profileData?.address?.address4,
                          address5: profileData?.address?.address5,
                          address6: profileData?.address?.address6,
                        });
                        setIsModalOpen(true);
                      }}
                    >
                      <FaEdit />
                    </button>
                  </div>
                  <div className="profile-card-body">
                    <div className="profile-detail-item">
                      <span>Father's Name</span>
                      <p>{profileData?.resource?.fathersName}</p>
                    </div>
                    {profileData?.resource?.spouseName && (
                      <div className="profile-detail-item">
                        <span>Spouse Name</span>
                        <p>{profileData.resource.spouseName}</p>
                      </div>
                    )}
                    <div className="profile-detail-item">
                      <span>Marital Status</span>
                      <p>{profileData?.resource?.maritalStatus}</p>
                    </div>
                    {profileData?.resource?.bloodGroup && (
                      <div className="profile-detail-item">
                        <span>Blood Group</span>
                        <p className="profile-blood-group">{profileData.resource.bloodGroup}</p>
                      </div>
                    )}
                    {profileData?.resource?.education && (
                      <div className="profile-detail-item">
                        <span>Education</span>
                        <p>{profileData.resource.education}</p>
                      </div>
                    )}
                    {profileData?.resource?.panNumber && (
                      <div className="profile-detail-item">
                        <span>PAN Number</span>
                        <p>{profileData.resource.panNumber}</p>
                      </div>
                    )}
                    {profileData?.resource?.aadharNumber && (
                      <div className="profile-detail-item">
                        <span>Aadhaar Number</span>
                        <p>{profileData.resource.aadharNumber}</p>
                      </div>
                    )}
                    {profileData?.resource?.uanNumber && (
                      <div className="profile-detail-item">
                        <span>UAN Number</span>
                        <p>{profileData.resource.uanNumber}</p>
                      </div>
                    )}
                    {profileData?.address?.address1 && (
                      <div className="profile-detail-item">
                        <span>Address </span>
                        <p>{profileData?.address?.address1}</p>
                        <p>{profileData?.address?.address2}</p>
                        <p>{profileData?.address?.address3}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="profile-info-card">
                  <div className="profile-card-header">
                    <h3>Work Experience</h3>
                    <button 
                      className="profile-edit-icon-btn"
                      onClick={() => {
                        setActiveForm('experience');
                        setFormData({});
                        setIsModalOpen(true);
                      }}
                    >
                      Add Experience
                    </button>
                  </div>
                  <div className="profile-card-body">
                    {profileData?.experienceDetails?.length > 0 ? (
                      profileData.experienceDetails.map((exp) => (
                        <div key={exp.expId} className="profile-experience-item">
                          <div className="profile-experience-header">
                            <h4>{exp.previousCompanyName}</h4>
                            <div className="profile-experience-actions">
                              <button 
                                className="profile-edit-icon-btn profile-small"
                                onClick={() => {
                                  setActiveForm('experience-edit');
                                  setFormData({
                                    expId: exp.expId,
                                    previousCompanyName: exp.previousCompanyName,
                                    previousDesignation: exp.previousDesignation,
                                    experience: exp.experience,
                                    startDate: exp.startDate?.split('T')[0],
                                    endDate: exp.endDate?.split('T')[0]
                                  });
                                  setIsModalOpen(true);
                                }}
                              >
                                <FaEdit />
                              </button>
                              <button 
                                className="profile-delete-icon-btn profile-small"
                                onClick={() => handleDeleteExperience(exp.expId)}
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </div>
                          <p><strong>Designation:</strong> {exp.previousDesignation}</p>
                          <p><strong>Experience:</strong> {exp.experience} years</p>
                          <p><strong>Period:</strong> {new Date(exp.startDate).toLocaleDateString()} - {exp.endDate ? new Date(exp.endDate).toLocaleDateString() : 'Present'}</p>
                        </div>
                      ))
                    ) : (
                      <p>No experience details added yet.</p>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="profile-tab-content profile-full-width">
                {activeTab === "bank" && (
                  <div className="profile-tab-panel">
                    <div className="profile-panel-header">
                      <h3>Bank & Payroll Information</h3>
                      <button 
                        className="profile-edit-icon-btn"
                        onClick={() => {
                          setActiveForm('bank');
                          setFormData({
                            bankName: profileData?.payroll?.bankName,
                            accountNumber: profileData?.payroll?.accountNo,
                            ifscCode: profileData?.payroll?.ifscCode,
                            netSalary: profileData?.payroll?.netPayable,
                            pfDeduction: profileData?.payroll?.pf,
                            tdsDeduction: profileData?.payroll?.tdsdeductionAmount
                          });
                          setIsModalOpen(true);
                        }}
                      >
                        <FaEdit />
                      </button>
                    </div>
                    <div className="profile-grid-2">
                      <div className="profile-detail-card">
                        <label>Bank Name</label>
                        <p>{profileData?.payroll?.bankName || val("bankName")}</p>
                      </div>
                      <div className="profile-detail-card">
                        <label>Account Number</label>
                        <p>{profileData?.payroll?.accountNo || val("bankAccount")}</p>
                      </div>
                      <div className="profile-detail-card">
                        <label>IFSC Code</label>
                        <p>{profileData?.payroll?.ifscCode || val("ifsc")}</p>
                      </div>
                      <div className="profile-detail-card">
                        <label>Net Salary</label>
                        <p>₹{profileData?.payroll?.netPayable || val("monthlySalary")}</p>
                      </div>
                      <div className="profile-detail-card">
                        <label>PF Deduction</label>
                        <p>₹{profileData?.payroll?.pf || "0.00"}</p>
                      </div>
                      <div className="profile-detail-card">
                        <label>TDS Deduction</label>
                        <p>₹{profileData?.payroll?.tdsdeductionAmount || "0.00"}</p>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "attendance" && (
                  <div className="profile-tab-panel">
                    <div className="profile-panel-header">
                      <h3>Attendance & Leave</h3>
                    </div>
                    <div className="profile-grid-2">
                      <div className="profile-detail-card profile-highlight-card">
                        <label>Salary Days This Month</label>
                        <p className="profile-highlight-value">{profileData?.attendance?.salaryDays || val("attendanceThisMonth")} days</p>
                      </div>
                      <div className="profile-detail-card profile-highlight-card">
                        <label>Total Leave Allowed</label>
                        <p className="profile-highlight-value">{leaveInfo.totalLeaveAllowed} days</p>
                      </div>
                      <div className="profile-detail-card profile-highlight-card">
                        <label>Leave Taken</label>
                        <p className={`profile-highlight-value ${leaveInfo.isOverLimit ? 'profile-text-danger' : ''}`}>
                          {leaveInfo.leaveTaken} days
                          {leaveInfo.isOverLimit && " ⚠️ Exceeded Limit"}
                        </p>
                      </div>
                      <div className="profile-detail-card profile-highlight-card">
                        <label>Remaining Leave</label>
                        <p className="profile-highlight-value">{leaveInfo.remainingLeave} days</p>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "expenses" && profileData?.expenses && (
                  <div className="profile-tab-panel">
                    <div className="profile-panel-header">
                      <h3>Expense Management</h3>
                    </div>
                    <div className="profile-grid-2">
                      {profileData.expenses.map((expense) => (
                        <div key={expense.expenseId} className="profile-detail-card profile-highlight-card">
                          <label>{expense.expenseType}</label>
                          <p className="profile-highlight-value">₹{expense.amountSpent}</p>
                          <p className="profile-expense-detail">{expense.merchantName} • {expense.dateofExpense}</p>
                          <p className={`profile-status-${expense.approvalStatus.toLowerCase()}`}>
                            {expense.approvalStatus}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === "travel" && profileData?.trips && (
                  <div className="profile-tab-panel">
                    <div className="profile-panel-header">
                      <h3>Travel Information</h3>
                    </div>
                    <div className="profile-grid-2">
                      {profileData.trips.map((trip) => (
                        <div key={trip.tripId} className="profile-detail-card profile-highlight-card">
                          <label>{trip.tripName}</label>
                          <p className="profile-highlight-value">{trip.purposeofTravel}</p>
                          <p className="profile-travel-detail">{trip.tripStartDate} to {trip.tripEndDate}</p>
                          <p className="profile-travel-detail">Manager: {trip.managersName}</p>
                          <p className={`profile-status-${trip.approvalStatus.toLowerCase()}`}>
                            {trip.approvalStatus}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === "approvals" && profileData?.approvals && (
                  <div className="profile-tab-panel">
                    <div className="profile-panel-header">
                      <h3>Pending Approvals</h3>
                    </div>
                    <div className="profile-grid-2">
                      {profileData.approvals.tripApprovals.map((approval) => (
                        <div key={approval.tripId} className="profile-detail-card profile-highlight-card">
                          <div className="profile-approval-header">
                            <span className="profile-approval-type-badge profile-trip-badge">Trip</span>
                            <span className={`profile-status profile-status-${approval.status.toLowerCase()}`}>
                              {approval.status}
                            </span>
                          </div>
                          <label>{approval.tripName}</label>
                          <p className="profile-approval-detail">Type: {approval.type}</p>
                          <p className="profile-approval-date">
                            Created: {new Date(approval.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                      
                      {profileData.approvals.expenseApprovals.map((approval) => (
                        <div key={approval.expenseId} className="profile-detail-card profile-highlight-card">
                          <div className="profile-approval-header">
                            <span className="profile-approval-type-badge profile-expense-badge">Expense</span>
                            <span className={`profile-status profile-status-${approval.status.toLowerCase()}`}>
                              {approval.status}
                            </span>
                          </div>
                          <label>Expense {approval.expenseId}</label>
                          <p className="profile-approval-detail">Type: {approval.type}</p>
                          <p className="profile-approval-detail">Trip ID: {approval.tripId}</p>
                          <p className="profile-approval-date">
                            Created: {new Date(approval.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                      
                      {profileData.approvals.tripApprovals.length === 0 && profileData.approvals.expenseApprovals.length === 0 && (
                        <div className="profile-detail-card profile-full-width">
                          <div className="profile-no-approvals">
                            <FaCheckCircle className="profile-no-approvals-icon" />
                            <p>No pending approvals</p>
                            <span>All caught up! There are no pending approvals at the moment.</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* {activeTab === "documents" && (
                  <div className="profile-tab-panel">
                    <div className="profile-panel-header">
                      <h3>Documents Management</h3>
                      <div className="profile-panel-actions">
                        <button 
                          className="profile-btn-primary"
                          onClick={() => {
                            console.log('🔄 Manually refreshing documents list...');
                            loadUserDocuments();
                          }}
                          disabled={documentsLoading}
                        >
                          {documentsLoading ? 'Refreshing...' : 'Refresh Documents'}
                        </button>
                      </div>
                    </div>
                    
                    {documentsLoading ? (
                      <div className="profile-loading-documents">
                        <div className="profile-loading-spinner"></div>
                        <p>Loading your documents...</p>
                      </div>
                    ) : (
                      <>
                        <div className="profile-documents-info">
                          <p className="profile-documents-count">
                            {documents.filter(doc => doc.uploaded).length} of {documents.length} documents uploaded
                          </p>
                        </div>
                        
                        <div className="profile-documents-grid">
                          {documents
                            .filter(doc => !doc.uploaded) 
                            .map((doc) => (
                              <DocumentCard
                                key={doc.documentId}
                                doc={doc}
                                onUpload={handleDocumentUpload}
                                onDelete={handleDocumentDelete}
                                onView={handleDocumentView}
                              />
                            ))
                          }
                        </div>

                   
                        {documents.filter(doc => doc.uploaded).length > 0 && (
                          <div className="profile-uploaded-documents-section">
                            <h4>Uploaded Documents</h4>
                            <div className="profile-documents-grid">
                              {documents
                                .filter(doc => doc.uploaded)
                                .map((doc) => (
                                  <DocumentCard
                                    key={doc.documentId}
                                    doc={doc}
                                    onUpload={handleDocumentUpload}
                                    onDelete={handleDocumentDelete}
                                    onView={handleDocumentView}
                                  />
                                ))
                              }
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    
                    {!documentsLoading && documents.filter(doc => !doc.uploaded).length === 0 && (
                      <div className="profile-all-documents-uploaded">
                        <FaCheckCircle className="profile-success-icon" />
                        <h4>All documents uploaded!</h4>
                        <p>You have successfully uploaded all required documents.</p>
                      </div>
                    )}
                  </div>
                )} */}


{activeTab === "documents" && (
  <div className="profile-tab-panel">
    <DocumentsSection
      userId={profileData?.resource?.mav_UserID}
      organisationId={profileData?.resource?.organisationId} // ADD THIS
      documents={documents}
      documentsLoading={documentsLoading}
      onRefreshDocuments={loadUserDocuments}
      onUploadDocument={handleDocumentUpload}
      onDeleteDocument={handleDocumentDelete}
      onViewDocument={handleDocumentView}
      // REMOVE onSaveQualifications - let it use localStorage fallback
    />
  </div>
)}
                {activeTab === "assets" && profileData?.assetsDetails && (
                  <div className="profile-tab-panel">
                    <div className="profile-panel-header">
                      <h3>Assets Assigned</h3>
                      <button 
                        className="profile-edit-icon-btn"
                        onClick={() => {
                          setActiveForm('assets');
                          const asset = profileData?.assetsDetails?.[0];
                          setFormData({
                            assetType: asset?.assetType,
                            brandModel: asset?.brandModel,
                            serialNo: asset?.serialNo,
                            assetCategory: asset?.assetCategory,
                            assetCondition: asset?.asset_Condition,
                            warrantyExpiry: asset?.warranty_Expiry,
                            notes: asset?.remarks || ""
                          });
                          setIsModalOpen(true);
                        }}
                      >
                        <FaEdit />
                      </button>
                    </div>
                    <div className="profile-grid-2">
                      {profileData.assetsDetails.map((asset) => (
                        <div key={asset.assetId} className="profile-detail-card">
                          <label>{asset.assetType}</label>
                          <p><strong>Brand:</strong> {asset.brandModel}</p>
                          <p><strong>Serial No:</strong> {asset.serialNo}</p>
                          <p><strong>Category:</strong> {asset.assetCategory}</p>
                          <p className={`profile-condition-${asset.asset_Condition?.toLowerCase()?.replace(' ', '-')}`}>
                            <strong>Condition:</strong> {asset.asset_Condition}
                          </p>
                          {asset.warranty_Expiry && (
                            <p><strong>Warranty:</strong> {asset.warranty_Expiry}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === "leaveInfo" && (
                  <div className="profile-tab-panel">
                    <div className="profile-panel-header">
                      <h3>Leave Info</h3>
                    </div>

                    {leaveInfoLoading && (
                      <p>Loading leave info...</p>
                    )}

                    {leaveInfoError && (
                      <p className="profile-text-danger">{leaveInfoError}</p>
                    )}

                    {leaveInfoData && (
                      <>
                        {/* Yearly Section */}
                        <div className="profile-info-card">
                          <div className="profile-card-header">
                            <h4>Yearly Leave Balance ({leaveInfoData.year})</h4>
                          </div>
                          <div className="profile-card-body">
                            <div className="profile-grid-2">
                              {leaveInfoData.yearly.balance.map((item) => (
                                <div key={item.id} className="profile-detail-card profile-highlight-card">
                                  <label>{item.name}</label>
                                  <p><strong>Credit:</strong> {item.credit}</p>
                                  <p><strong>Debit:</strong> {item.debit}</p>
                                  <p><strong>Balance:</strong> {item.balance}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Monthly Section */}
                        <div className="profile-info-card">
                          <div className="profile-card-header">
                            <h4>Monthly Leave Balance ({leaveInfoData.month}/{leaveInfoData.year})</h4>
                          </div>
                          <div className="profile-card-body">
                            <div className="profile-grid-2">
                              {leaveInfoData.monthly.balance.map((item) => (
                                <div key={item.id} className="profile-detail-card profile-highlight-card">
                                  <label>{item.name}</label>
                                  <p><strong>Credit:</strong> {item.credit}</p>
                                  <p><strong>Debit:</strong> {item.debit}</p>
                                  <p><strong>Balance:</strong> {item.balance}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {!leaveInfoLoading && !leaveInfoError && !leaveInfoData && (
                      <p>No leave info available.</p>
                    )}
                  </div>
                )}

             {activeTab === "socialMedia" && (
  <div className="profile-tab-panel">
    <div className="profile-panel-header">
      <h3>Social Media Links</h3>
      <button 
        className="profile-edit-icon-btn"
        onClick={() => {
          setActiveForm('socialMedia');
          setFormData({
            linkedIn: profileData?.resource?.linkedIn || profileData?.socialMediaLinks?.linkedIn,
            facebook: profileData?.resource?.facebook || profileData?.socialMediaLinks?.facebook,
            instagram: profileData?.resource?.instagram || profileData?.socialMediaLinks?.instagram,
            github: profileData?.resource?.github || profileData?.socialMediaLinks?.github,
            youtube: profileData?.resource?.youtube || profileData?.socialMediaLinks?.youtube
          });
          setIsModalOpen(true);
        }}
      >
        <FaEdit />
      </button>
    </div>
    
    <div className="profile-grid-2">
      <div className="profile-detail-card">
        <div className="profile-social-media-item">
          <div className="profile-social-icon">
            <FaLinkedin />
          </div>
          <div className="profile-social-content">
            <label>LinkedIn</label>
            {profileData?.resource?.linkedIn || profileData?.socialMediaLinks?.linkedIn ? (
              <a 
                href={profileData?.resource?.linkedIn || profileData?.socialMediaLinks?.linkedIn} 
                target="_blank" 
                rel="noopener noreferrer"
                className="profile-social-link"
              >
                View Profile
              </a>
            ) : (
              <p className="profile-text-muted">No LinkedIn URL added</p>
            )}
          </div>
        </div>
      </div>

      <div className="profile-detail-card">
        <div className="profile-social-media-item">
          <div className="profile-social-icon">
            <FaFacebook />
          </div>
          <div className="profile-social-content">
            <label>Facebook</label>
            {profileData?.resource?.facebook || profileData?.socialMediaLinks?.facebook ? (
              <a 
                href={profileData?.resource?.facebook || profileData?.socialMediaLinks?.facebook} 
                target="_blank" 
                rel="noopener noreferrer"
                className="profile-social-link"
              >
                View Profile
              </a>
            ) : (
              <p className="profile-text-muted">No Facebook URL added</p>
            )}
          </div>
        </div>
      </div>

      <div className="profile-detail-card">
        <div className="profile-social-media-item">
          <div className="profile-social-icon">
            <FaInstagram />
          </div>
          <div className="profile-social-content">
            <label>Instagram</label>
            {profileData?.resource?.instagram || profileData?.socialMediaLinks?.instagram ? (
              <a 
                href={profileData?.resource?.instagram || profileData?.socialMediaLinks?.instagram} 
                target="_blank" 
                rel="noopener noreferrer"
                className="profile-social-link"
              >
                View Profile
              </a>
            ) : (
              <p className="profile-text-muted">No Instagram URL added</p>
            )}
          </div>
        </div>
      </div>



        <div className="profile-detail-card">
        <div className="profile-social-media-item">
          <div className="profile-social-icon">
            <FaYoutube />
          </div>
          <div className="profile-social-content">
            <label>Youtube</label>
            {profileData?.resource?.youtube || profileData?.socialMediaLinks?.youtube ? (
              <a 
                href={profileData?.resource?.youtube || profileData?.socialMediaLinks?.youtube} 
                target="_blank" 
                rel="noopener noreferrer"
                className="profile-social-link"
              >
                View Profile
              </a>
            ) : (
              <p className="profile-text-muted">No Youtube URL added</p>
            )}
          </div>
        </div>
      </div>

      <div className="profile-detail-card">
        <div className="profile-social-media-item">
          <div className="profile-social-icon">
            <FaGithub />
          </div>
          <div className="profile-social-content">
            <label>GitHub</label>
            {profileData?.resource?.github || profileData?.socialMediaLinks?.github ? (
              <a 
                href={profileData?.resource?.github || profileData?.socialMediaLinks?.github} 
                target="_blank" 
                rel="noopener noreferrer"
                className="profile-social-link"
              >
                View Profile
              </a>
            ) : (
              <p className="profile-text-muted">No GitHub URL added</p>
            )}
          </div>
        </div>
      </div>
    </div>

    {(!profileData?.resource?.linkedIn && !profileData?.resource?.facebook && 
      !profileData?.resource?.instagram && !profileData?.resource?.github &&
      !profileData?.socialMediaLinks?.linkedIn && !profileData?.socialMediaLinks?.facebook &&
      !profileData?.socialMediaLinks?.instagram && !profileData?.socialMediaLinks?.github) && (
      <div className="profile-empty-state">
        <div className="profile-empty-icon">🌐</div>
        <h4>No Social Media Links Added</h4>
        <p>Add your social media profiles to connect with colleagues</p>
        <button 
          className="profile-btn-primary"
          onClick={() => {
            setActiveForm('socialMedia');
            setFormData({});
            setIsModalOpen(true);
          }}
        >
          <FaEdit /> Add Social Media Links
        </button>
      </div>
    )}
  </div>
)}

              </div>
            )}
          </div>
        </div>

        <FormModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setActiveForm('');
            setFormData({});
          }}
          formType={activeForm}
          initialData={formData}
          onSubmit={handleFormSubmit}
        />

        <DeleteConfirmationModal
          isOpen={deleteModal.isOpen}
          onClose={() => setDeleteModal({ isOpen: false, itemName: '', onConfirm: null })}
          onConfirm={deleteModal.onConfirm}
          itemName={deleteModal.itemName}
        />

        <DocumentUploadModal
          isOpen={documentUploadModal.isOpen}
          onClose={() => setDocumentUploadModal({ isOpen: false, documentType: '' })}
          documentType={documentUploadModal.documentType}
          onUpload={handleDocumentUpload}
        />

        {loading && (
          <div className="profile-loading-overlay">
            <Sample14 />
          </div>
        )}
      </div>
    </div>
  );
}